import type { Inbox } from '../api/types';

/**
 * What a composer may offer for a given inbox. Every flag is a claim about what
 * the SERVER can actually deliver on that channel — offering a control the channel
 * drops is worse than not showing it, because the agent watches the message send
 * and never learns the file went nowhere.
 */
export interface ChannelCapabilities {
  /** Styled body: bold, lists, links — sent as the html part. Email only. */
  richText: boolean;
  /** Files travel with the message (mms / media / mail attachment). */
  attachments: boolean;
  /** Threads carry a subject worth showing and editing. */
  subject: boolean;
  /** Extra recipients on the same message. */
  cc: boolean;
}

const PLAIN: ChannelCapabilities = {
  richText: false,
  attachments: false,
  subject: false,
  cc: false,
};

const CAPABILITIES: Record<Inbox['channelType'], ChannelCapabilities> = {
  email: { richText: true, attachments: true, subject: true, cc: true },
  // Twilio sends media as MMS, WhatsApp and Telegram as media messages.
  twilio: { ...PLAIN, attachments: true },
  whatsapp: { ...PLAIN, attachments: true },
  telegram: { ...PLAIN, attachments: true },
  // RingCentral's send path is text-only: an attachment on an SMS is uploaded,
  // stored, and then never sent.
  ringcentral: PLAIN,
  // Widget v1 is text both ways; visitor uploads are a later, separate surface.
  website: PLAIN,
  // The Messenger Platform carries an attachment as its own message, one file
  // each — the send path posts them after the text.
  facebook: { ...PLAIN, attachments: true },
  instagram: { ...PLAIN, attachments: true },
};

export const capabilitiesOf = (inbox: Inbox | undefined): ChannelCapabilities =>
  inbox ? (CAPABILITIES[inbox.channelType] ?? PLAIN) : PLAIN;
